import React from 'react'
import { resolveThemeMode } from '../lib/themeMode'
import { useSettings } from '../stateData/settings'
import { useTheme } from './useTheme'
import { useNativeApp } from './useNativeApp'
import { useSignupSession } from './useSignupSession'

export const useResolvedThemeMode = (): 'light' | 'dark' => {
  const settingsState = useSettings()
  const theme = useTheme()
  const isNative = useNativeApp()
  const { isHpx } = useSignupSession()

  const {
    enableDarkModeSignup,
    forcedThemeModeEnrollment,
    darkModeEnabledApps
  } = settingsState?.data || {}

  const matchMediaMode = () => {
    if (globalThis?.matchMedia?.('(prefers-color-scheme: dark)')?.matches) {
      return 'dark'
    }

    return 'light'
  }

  const nativeEnabled = darkModeEnabledApps?.includes('native')
    ? isNative
    : true
  const hpxEnabled = darkModeEnabledApps?.includes('hpx') ? isHpx : true

  const darkModeEnabled = enableDarkModeSignup && nativeEnabled && hpxEnabled

  const appThemeMode = theme?.getUserThemeMode() ?? matchMediaMode()
  const resolvedMode = resolveThemeMode(
    darkModeEnabled,
    appThemeMode,
    forcedThemeModeEnrollment
  )
  const isStyleguide = !!(globalThis as Record<string, unknown>).__STYLEGUIDE__

  const getStyleguideMode = (): 'light' | 'dark' | null => {
    const mode = sessionStorage.getItem('styleguide-theme')
    return mode === 'dark' || mode === 'light' ? mode : null
  }

  const [styleguideMode, setStyleguideMode] = React.useState<
    'light' | 'dark' | null
  >(() => (isStyleguide ? getStyleguideMode() : null))

  React.useEffect(() => {
    if (!isStyleguide) return

    const handler = () => {
      setStyleguideMode(getStyleguideMode())
    }

    globalThis.addEventListener('styleguide-theme-change', handler)

    return () => {
      globalThis.removeEventListener('styleguide-theme-change', handler)
    }
  }, [isStyleguide])

  if (isStyleguide && styleguideMode) {
    return styleguideMode
  }

  return resolvedMode
}
